import React, { useCallback, useState } from 'react';
import { API_BASE_URL } from '../../config/apiConfig';
import { useTheme } from '../../contexts/ThemeContext';
import { cn } from '../../lib/utils';

const makeApiCall = async (method, url, data = null) => {
  const token = localStorage.getItem('authToken');
  const savedUser = localStorage.getItem('user');
  let userId = null;

  if (savedUser && savedUser !== 'undefined' && savedUser !== 'null') {
    try {
      const parsedUser = JSON.parse(savedUser);
      userId = parsedUser.id?.toString() || null;
    } catch {
      userId = null;
    }
  }

  if (!token) {
    throw new Error('Authentication required. Please log in again.');
  }

  const response = await fetch(`${API_BASE_URL}${url}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(userId && { 'user-id': userId }),
    },
    ...(data && { body: JSON.stringify(data) }),
  });

  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(payload?.message || 'Request failed');
  }
  return payload;
};

const statusStyles = {
  applied: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200',
  pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  unknown: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200',
};

/**
 * Settings → Database Migrations
 * Password-gated UI to run allowlisted production SQL scripts.
 */
const DatabaseMigrationsPage = () => {
  const { theme } = useTheme();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [sessionPassword, setSessionPassword] = useState('');
  const [migrations, setMigrations] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [runningId, setRunningId] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const loadMigrations = useCallback(async (pwd) => {
    setLoadingList(true);
    setError('');
    try {
      const list = await makeApiCall('POST', '/database-migrations/list', {
        password: pwd,
      });
      setMigrations(Array.isArray(list) ? list : []);
    } catch (err) {
      setError(err?.message || 'Failed to load migrations');
      setMigrations([]);
    } finally {
      setLoadingList(false);
    }
  }, []);

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!password.trim()) {
      setPasswordError('Please enter a password');
      return;
    }

    try {
      setPasswordError('');
      const response = await makeApiCall(
        'POST',
        '/database-migrations/validate-password',
        { password },
      );
      if (!response.valid) {
        setPasswordError('Incorrect password. Please try again.');
        setPassword('');
        return;
      }
      setSessionPassword(password);
      setIsAuthenticated(true);
      setPassword('');
      await loadMigrations(password);
    } catch (err) {
      setPasswordError(err?.message || 'Incorrect password. Please try again.');
      setPassword('');
    }
  };

  const handleRun = async (migrationId) => {
    const confirmed = window.confirm(
      'Run this migration on the current database?\n\nThis will change production schema/data. Continue only if you intend to apply it.',
    );
    if (!confirmed) return;

    setRunningId(migrationId);
    setResult(null);
    setError('');
    try {
      const response = await makeApiCall('POST', '/database-migrations/run', {
        password: sessionPassword,
        migrationId,
      });
      setResult(response);
      await loadMigrations(sessionPassword);
    } catch (err) {
      setError(err?.message || 'Migration failed');
    } finally {
      setRunningId(null);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="h-full min-h-0 flex items-center justify-center p-4 md:p-6">
        <form
          onSubmit={handlePasswordSubmit}
          className={cn(
            'w-full max-w-md rounded-xl border p-6 shadow-sm',
            theme.border.light,
            theme.colors.surface,
          )}
        >
          <h1 className={cn('text-xl font-semibold', theme.text.primary)}>
            Database Migrations
          </h1>
          <p className={cn('text-sm mt-2 mb-4', theme.text.secondary)}>
            Enter the admin password to view and run allowlisted production
            migrations.
          </p>
          <label className={cn('block text-sm mb-1', theme.text.secondary)}>
            Password
          </label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={cn(
              'w-full rounded-lg border px-3 py-2 text-sm mb-2',
              theme.border.light,
              theme.colors.surface,
              theme.text.primary,
            )}
            autoFocus
          />
          {passwordError && (
            <p className="text-sm text-red-600 dark:text-red-400 mb-2">
              {passwordError}
            </p>
          )}
          <button
            type="submit"
            className="mt-2 w-full rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"
          >
            Unlock
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="h-full min-h-0 flex flex-col p-4 md:p-6 gap-4">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className={cn('text-xl font-semibold', theme.text.primary)}>
            Database Migrations
          </h1>
          <p className={cn('text-sm mt-1', theme.text.secondary)}>
            Run allowlisted production SQL scripts. Only listed migrations can
            be executed — free-form SQL is not allowed.
          </p>
        </div>
        <button
          type="button"
          onClick={() => loadMigrations(sessionPassword)}
          disabled={loadingList}
          className={cn(
            'rounded-lg border px-3 py-2 text-sm font-medium',
            theme.border.light,
            theme.text.primary,
            'hover:bg-slate-50 dark:hover:bg-slate-800',
          )}
        >
          {loadingList ? 'Refreshing…' : 'Refresh status'}
        </button>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200">
          {error}
        </div>
      )}

      {result && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 dark:border-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-200">
          {result.message}
          {result.statementsExecuted != null && (
            <span className="ml-1">({result.statementsExecuted} statements)</span>
          )}
        </div>
      )}

      <div className="space-y-3">
        {migrations.length === 0 && !loadingList && (
          <p className={cn('text-sm', theme.text.secondary)}>
            No allowlisted migrations found.
          </p>
        )}

        {migrations.map((migration) => {
          const isApplied = migration.status === 'applied';
          const isRunning = runningId === migration.id;
          return (
            <div
              key={migration.id}
              className={cn(
                'rounded-xl border p-4',
                theme.border.light,
                theme.colors.surface,
              )}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h2 className={cn('text-base font-semibold', theme.text.primary)}>
                      {migration.title}
                    </h2>
                    <span
                      className={cn(
                        'rounded-full px-2.5 py-0.5 text-xs font-medium capitalize',
                        statusStyles[migration.status] || statusStyles.unknown,
                      )}
                    >
                      {migration.status}
                    </span>
                  </div>
                  <p className={cn('text-sm mt-1', theme.text.secondary)}>
                    {migration.description}
                  </p>
                  <p className={cn('text-xs mt-2 font-mono', theme.text.muted)}>
                    {migration.relativePath}
                    {!migration.fileExists && ' (file missing on server)'}
                  </p>
                </div>
                <button
                  type="button"
                  disabled={
                    isApplied ||
                    isRunning ||
                    !migration.fileExists ||
                    loadingList
                  }
                  onClick={() => handleRun(migration.id)}
                  className={cn(
                    'rounded-lg px-4 py-2 text-sm font-semibold text-white',
                    isApplied || !migration.fileExists
                      ? 'bg-slate-400 cursor-not-allowed'
                      : 'bg-blue-600 hover:bg-blue-700',
                  )}
                >
                  {isRunning ? 'Running…' : isApplied ? 'Already applied' : 'Run migration'}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DatabaseMigrationsPage;
